import { IconFont, Tooltip } from '@byecode/ui'
import type { Field, FilterableProtocol, FilterFormType, Sorter, TableColumns } from '@lighthouse/core'
import { type DataSourceAbstract, DataSourceType, DevicePreviewEnum } from '@lighthouse/core'
import type { AppendParams, ImportParams, InsertDirection } from '@lighthouse/shared'
import { notDataSettingInnerTypes, useDataSourceContext } from '@lighthouse/shared'
import React, { useLayoutEffect, useRef, useState } from 'react'

import DataSourceAppender from '../DataSourceAppender'
import { DataSourceSetting } from '../DataSourceSetting'
import * as SC from './styles'

interface DataSourceSettingToolBarProps {
    searchVal: string
    envId: string
    dataSource: DataSourceAbstract
    dataSourceList: DataSourceAbstract[]
    filter?: FilterFormType
    sorts?: Sorter[]
    onChangeSorter: (data?: Sorter[]) => void
    onChangeDisplay: (data?: TableColumns) => Promise<boolean>
    onChangeFilter: (data: FilterableProtocol['filter']) => void
    onChangeSearch: (val: string) => void
    onUpdateField: (fieldId: string, data: Field) => Promise<boolean>
    onDeleteField: (field: Field) => Promise<boolean>
    onCreateField: (data: Field, sourceId: string, direction: InsertDirection) => Promise<boolean>
    onDataAppended?: (params: AppendParams) => Promise<boolean>
    onDataCreate?: (params: ImportParams) => Promise<boolean>
    onMarkdownImported?: () => void
    onVisibleJoinConfig: (val: boolean) => void
    onEditAggregator: () => void
}

export const DataSourceSettingToolBar: React.FC<DataSourceSettingToolBarProps> = ({
    searchVal,
    envId,
    dataSource,
    dataSourceList,
    filter,
    sorts,
    onChangeSorter,
    onChangeDisplay,
    onChangeFilter,
    onChangeSearch,
    onUpdateField,
    onDeleteField,
    onCreateField,
    onDataAppended,
    onDataCreate,
    onMarkdownImported,
    onVisibleJoinConfig,
    onEditAggregator
}) => {
    const { appId, id, type, name, sync } = dataSource
    const [previewType, setPreviewType] = useState(DevicePreviewEnum.DESKTOP)
    const headerRef = useRef<HTMLDivElement>(null)
    const { visibleJoinConfigure, onVisibleJoinConfigureChange } = useDataSourceContext()
    const isJoinDataSource = type === DataSourceType.joinDataSource
    const isCreateJoinDataSource = type !== DataSourceType.joinDataSource && type !== DataSourceType.aggregateDataSource
    const isAggregateDataSource = type === DataSourceType.aggregateDataSource

    useLayoutEffect(() => {
        const resizeHandler: ResizeObserverCallback = ([e]) => {
            const { width } = e.target.getBoundingClientRect()
            if (width >= 724) {
                setPreviewType(DevicePreviewEnum.DESKTOP)
                return
            }
            setPreviewType(DevicePreviewEnum.MOBILE)
        }
        const observer = new ResizeObserver(resizeHandler)
        if (headerRef.current) {
            observer.observe(headerRef.current)
            return () => {
                observer.disconnect()
            }
        }
    }, [])

    return (
        <SC.DataSourceHeader ref={headerRef}>
            <SC.DataSourceTitle>{name}</SC.DataSourceTitle>
            <SC.Handle>
                <DataSourceSetting
                    searchVal={searchVal}
                    dataSourceInfo={dataSource}
                    dataSourceList={dataSourceList}
                    filter={filter}
                    sorts={sorts}
                    noSettingInnerType={notDataSettingInnerTypes}
                    previewType={previewType}
                    onChangeSorter={onChangeSorter}
                    onChangeDisplay={onChangeDisplay}
                    onChangeFilter={onChangeFilter}
                    onChangeSearch={onChangeSearch}
                    onUpdateField={onUpdateField}
                    onDeleteField={onDeleteField}
                    onCreateField={onCreateField}
                />
                <DataSourceAppender
                    name={name}
                    appId={appId}
                    envId={envId}
                    dataSource={dataSource}
                    searchVal={searchVal}
                    // previewType={previewType}
                    onAppend={onDataAppended}
                    onCreate={onDataCreate}
                    onMarkdownImported={onMarkdownImported}
                />
                {isCreateJoinDataSource && (
                    <SC.JoinCreateButton
                        size="sm"
                        icon={<IconFont type="RelationQuery" fill="var(--color-green-500)" />}
                        onClick={() => onVisibleJoinConfig(true)}
                    >
                        {previewType === DevicePreviewEnum.DESKTOP && '创建连接'}
                    </SC.JoinCreateButton>
                )}
                {isAggregateDataSource && (
                    <SC.EditButton
                        size="sm"
                        disabled={sync}
                        icon={<IconFont type="SettingsStylesA" fill="var(--color-gray-400)" />}
                        onClick={onEditAggregator}
                    >
                        {previewType === DevicePreviewEnum.DESKTOP && '聚合配置'}
                    </SC.EditButton>
                )}
                {isJoinDataSource && (
                    <Tooltip disabled={!sync} title="同步的关联表不可编辑">
                        <SC.EditButton
                            size="sm"
                            disabled={sync}
                            type="default"
                            icon={<IconFont type="LeftDrawer" />}
                            onClick={() => onVisibleJoinConfigureChange?.(!visibleJoinConfigure)}
                        >
                            {previewType === DevicePreviewEnum.DESKTOP && '连接配置'}
                        </SC.EditButton>
                    </Tooltip>
                )}
            </SC.Handle>
        </SC.DataSourceHeader>
    )
}
